import { useMemo, useCallback } from 'react';
import { DateTime } from 'luxon';
import {
  useWbsProjectLabelSearchLazyQuery,
  WbsLabelValueSearchResult,
  WbsProjectLabelSearchQueryResult,
  WbsLabelValueSearchInput,
  WbsProjectLabelSearchQuery,
  WbsProjectLabelSearchDocument
} from 'graphql/types';
import { FacetOption } from 'modules/notes/common';
import { SearchHandlerParams } from '../../LabelFacetDetails';

export const PROJECT_PAGE_COUNT = 50;

const getProjectOptions = (
  data: WbsProjectLabelSearchQuery | undefined
): WbsLabelValueSearchResult[] =>
  data?.wbsLabelValueSearch?.projects.items.filter(({ id }) => id) ?? [];

export const useProjectLabelSearch = (): {
  options: WbsLabelValueSearchResult[];
  loading: boolean;
  searchHandler: (params: SearchHandlerParams) => void;
  hasMore: boolean;
} => {
  const [getClients, { data, loading }] = useWbsProjectLabelSearchLazyQuery();

  const searchHandler = useCallback(
    ({ query }: SearchHandlerParams) => {
      const variables = {
        date: DateTime.local().toISODate(),
        input: {
          count: PROJECT_PAGE_COUNT,
          query
        }
      };
      getClients({ variables });
    },
    [getClients]
  );

  const projects = useMemo(() => getProjectOptions(data), [data]);

  return useMemo(
    () => ({
      searchHandler,
      loading,
      options: projects,
      hasMore: projects.length === PROJECT_PAGE_COUNT
    }),
    [projects, loading, searchHandler]
  );
};

export const searchProjects = ({
  client: apolloClient
}: {
  client: any;
}) => async (
  input: WbsLabelValueSearchInput
): Promise<WbsLabelValueSearchResult[]> => {
  const { data }: WbsProjectLabelSearchQueryResult = await apolloClient.query({
    query: WbsProjectLabelSearchDocument,
    variables: {
      date: DateTime.local().toISODate(),
      input: input
    }
  });

  return getProjectOptions(data);
};

const transformProject = (
  data: WbsProjectLabelSearchQuery | undefined
): FacetOption[] =>
  data?.wbsLabelValueSearch?.projects.items.map((project) => ({
    ...project,
    label: project.displayText,
    key: project.displayText,
    id: project.id || undefined
  })) ?? [];

export const searchProjectsByTerm = ({
  client: apolloClient
}: {
  client: any;
}) => async (searchTerm: string): Promise<FacetOption[]> => {
  const { data }: WbsProjectLabelSearchQueryResult = await apolloClient.query({
    query: WbsProjectLabelSearchDocument,
    variables: {
      date: DateTime.local().toISODate(),
      input: { count: 10, query: searchTerm }
    }
  });

  return transformProject(data);
};

export default useProjectLabelSearch;
